import { useMemo, useState } from 'react'
import { Combobox } from '@headlessui/react'
import { Field } from 'formular'
import cx from 'classnames'
import { Input } from 'components/inputs'
import { Icon } from 'components/dataDisplay'
import Option from './components/Option/Option'

import s from './InputWithAutocomplete.scss'


type AutocompleteProps = {
  size?: 48 | 56
  field: Field<string>
  label: Intl.Message | string
  isFetching: boolean
  onSelect: (value: any) => void
  onChange: (value: any) => void
  children: React.ReactElement<typeof Option>[]
  inputTestId?: string
  containerTestId?: string
}

const InputWithAutocomplete: React.FunctionComponent<AutocompleteProps> & { Option?: typeof Option } = ({ size = 48,
  field,
  label,
  onSelect,
  onChange,
  isFetching,
  children,
  inputTestId,
  containerTestId }) => {

  const [ activeOption, setActiveOption ] = useState(null)

  const options = useMemo(() => {
    if (!isFetching && children?.length === 0) {
      return null
    }

    if (isFetching) {
      return (
        <div className="flex justify-center items-center h-full">
          <Icon name="20/loader" />
        </div>
      )
    }

    return children
  }, [ isFetching, children ])

  const optionsClassName = cx(
    [ s.scrollableContainer ],
    'absolute left-0 w-full z-floating-header mt-12 shadow py-4 radius-5 bg-white'
  )

  return (
    <Combobox
      // The Combobox.Input overwrites (via forwardRef) field values to empty (see PF-1561), so we need a fallback to the field value
      value={activeOption || field.state.value}
      onChange={(value) => {
        setActiveOption(value)
        if (typeof onSelect === 'function') {
          onSelect(value)
        }
      }}
    >
      <div className="relative">
        <Combobox.Input
          as={Input}
          label={label}
          size={size}
          field={field}
          onChange={onChange}
          autoComplete="off"
          withCross
          data-testid={inputTestId}
        />
        {
          Boolean(options) && (
            <Combobox.Options
              className={optionsClassName}
              aria-busy={isFetching}
              data-testid={containerTestId}
            >
              {options}
            </Combobox.Options>
          )
        }
      </div>
    </Combobox>
  )
}

InputWithAutocomplete.Option = Option

export default InputWithAutocomplete
